import { memo, useReducer } from 'react';

import { ClockAlert, Gem, Package, SquarePen, Trash2 } from 'lucide-react';
import { omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

import { StoreProductForm } from '../form';
import { StoreProductFormDialog } from '../storeProductFormDialog/StoreProductFormDialog';

import css from './StoreProductCard.module.css';

import { storeService } from 'v2/api/storeService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { StoreProductModel } from 'v2/types/StoreModel';
import { fileSrc, formatNumber, handleHttpError } from 'v2/utils';

interface StoreProductCard {
  product: StoreProductModel;
  onRevalidate: VoidFunction;
}

export const StoreProductCard = memo<StoreProductCard>(({ product, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateStoreProduct', product.id],
    storeService.updateStoreProduct,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The product has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(
    ['deleteStoreProduct', product.id],
    storeService.deleteStoreProduct,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const handleSubmit = (values: StoreProductForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => resetForm());
  };

  return (
    <div className={css.storeProductCard}>
      <img className={css.storeProductImg} src={fileSrc(product.iconId)} alt={product.name} />

      <Chip className={css.storeProductType} label={product.type} color="secondary" size="small" />

      <div className={css.storeProductCardContent}>
        <div className={css.storeProductCardHeader}>
          <p className={css.storeProductName}>{product.name}</p>

          <p className={css.storeProductDescription}>{product.shortDescription}</p>

          <p className={css.storeProductPrice}>
            <Gem width={16} height={16} /> {formatNumber(product.price)}
          </p>
        </div>

        <div className={css.storeProductCardInfoContainer}>
          <p className={css.storeProductCardInfo}>
            <Package width={16} height={16} /> {product.count} in stock
          </p>

          <p className={css.storeProductCardInfo}>
            <ClockAlert width={16} height={16} /> {product.lockDays} lock days
          </p>
        </div>

        <div className={css.storeProductCardActions}>
          <Popconfirm message="Are you sure you want to delete this product?" onConfirm={triggerDelete}>
            <Button startIcon={<Trash2 width={18} />} variant="outlined" color="error" fullWidth>
              Delete
            </Button>
          </Popconfirm>

          <Button onClick={setOpen} startIcon={<SquarePen width={18} />} variant="outlined" color="warning">
            Edit
          </Button>
        </div>
      </div>

      <StoreProductFormDialog
        title="Edit Shop Product"
        isOpen={isOpen}
        onClose={setOpen}
        initialValues={omit(product, ['id'])}
        isSubmitting={isMutating}
        onSubmit={handleSubmit}
      />
    </div>
  );
});

StoreProductCard.displayName = 'StoreProductCard';
